import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, DollarSign, AlertCircle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const SalaryReport = () => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ 
    total_staff: 0, 
    total_gross_salary: 0, 
    total_deductions: 0, 
    total_net_salary: 0,
    paid_count: 0,
    pending_count: 0 
  });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = month ? `/api/salary/payroll?month=${month}` : `/api/salary/payroll`;
      const response = await apiClient.get(url);
      const data = response.data;
      setPayments(data.records || []);
      setSummary(data.summary || { 
        total_staff: 0, 
        total_gross_salary: 0, 
        total_deductions: 0, 
        total_net_salary: 0,
        paid_count: 0,
        pending_count: 0 
      });
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load salary data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Salary Report
          </CardTitle>
          <div className="flex gap-2 items-center">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
            <Button onClick={fetchReport} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchReport} className="ml-auto">Retry</Button>
            </div>
          )}

          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-600">Total Staff</p>
              <p className="text-2xl font-bold text-blue-700">{summary.total_staff}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-600">Gross Salary</p>
              <p className="text-2xl font-bold text-purple-700">₹{summary.total_gross_salary?.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600">Total Deductions</p>
              <p className="text-2xl font-bold text-red-700">₹{summary.total_deductions?.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-600">Net Salary</p>
              <p className="text-2xl font-bold text-green-700">₹{summary.total_net_salary?.toLocaleString('en-IN')}</p>
            </div>
          </div>

          <div className="flex gap-4 mb-4">
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Paid: {summary.paid_count}</span>
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">Pending: {summary.pending_count}</span>
          </div>

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Staff Name</th>
                <th className="text-left p-3">Month</th>
                <th className="text-right p-3">Basic</th>
                <th className="text-right p-3">Allowances</th>
                <th className="text-right p-3">Gross</th>
                <th className="text-right p-3">Deductions</th>
                <th className="text-right p-3">Net Salary</th>
                <th className="text-center p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center p-8 text-gray-500">Loading...</td></tr>
              ) : payments.length === 0 ? (
                <tr><td colSpan="8" className="text-center p-8 text-gray-500">No salary records found for the selected period</td></tr>
              ) : (
                payments.map((payment, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{payment.staff_name}</td>
                    <td className="p-3">{payment.month}</td>
                    <td className="p-3 text-right">₹{payment.basic_salary?.toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right">₹{payment.total_allowances?.toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right">₹{payment.payable_gross?.toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right text-red-600">₹{payment.total_deductions?.toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right font-semibold text-green-600">₹{payment.net_salary?.toLocaleString('en-IN')}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${payment.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {payment.payment_status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalaryReport;
